import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Button,
  Link,
  IconButton,
  InputBase,
} from "@mui/material";
import moment from "moment-timezone";
import { isMobile } from "react-device-detect";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Swal from "sweetalert2";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import Scanner from "components/Scanner";
import { PURPLE_MAIN, BG_TINT, BORDER, RADIUS_INPUT } from "components/bento/bentoTokens";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import PackageEditDialog from "components/PackageEditDialog";
import { GetDepartureCandidates, CreateDepartureBatch } from "api/departureBatch";
import { GetPackageDetailByTracking } from "api/packages";
import { playCorrectSound, playIncorrectSound, playAlertSound, getScanText } from "utils";
import useStationRefresh from "hooks/useStationRefresh";

// `refreshSignal` — bumped by the parent (scenes/departureBatch/index.jsx)
// whenever the History tab clears/cancels items on a batch, so packages that
// just became available again show up here without requiring a manual
// station re-select. Tabs stay mounted under TabsNavigator, so this can't
// rely on a remount.
const CreateTab = ({ refreshSignal }) => {
  const { t } = useTranslation();
  const { user: _user, defaultStation: _defaultStation } = useStationRefresh();
  const stationList = _user?.stations || [];

  const [selectedStationId, setSelectedStationId] = useState("");
  const [stationEligible, setStationEligible] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  // Every next station reachable from the selected station (per the route
  // topology, not just what's currently sitting there) — a sort/fan-out hub
  // like VTE splits toward several destinations (DKC, PHT, THK, ...) and a
  // single batch can only go to one, so this drives a destination filter.
  const [nextStationOptions, setNextStationOptions] = useState([]);
  const [nextStationFilter, setNextStationFilter] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trackingDetailNumber, setTrackingDetailNumber] = useState(null);
  const [editPackage, setEditPackage] = useState(null);
  const [searchTracking, setSearchTracking] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);

  const visibleRows = nextStationFilter
    ? rows.filter((r) => r.nextStationId === nextStationFilter)
    : rows;

  useEffect(() => {
    if (!selectedStationId && _defaultStation?.id) {
      setSelectedStationId(_defaultStation.id);
    }
  }, [_defaultStation, selectedStationId]);

  const refreshCandidates = (stationId) => {
    if (!stationId) return;
    setIsLoading(true);
    GetDepartureCandidates(stationId).then((res) => {
      if (!res.success) {
        // Distinct from "this station has no outbound leg" (a real business
        // state) — a failed request (403 missing permission, network error,
        // etc.) must not be silently displayed as if the station were fine.
        setLoadError(res.message || t("departureBatch.loadErrorGeneric"));
        setStationEligible(true);
        setNextStationOptions([]);
        setRows([]);
        setIsLoading(false);
        return;
      }
      setLoadError(null);
      setStationEligible(!!res.stationEligible);
      setNextStationOptions(Array.isArray(res.nextStations) ? res.nextStations : []);
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setNextStationFilter("");
    refreshCandidates(selectedStationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationId]);

  // Re-fetch (without resetting the destination filter) whenever the parent
  // signals that a batch's items changed elsewhere. `refreshSignal` starts
  // at 0/undefined, so this doesn't double-fetch on first mount.
  useEffect(() => {
    if (refreshSignal) refreshCandidates(selectedStationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Default: everything currently visible (post-filter) selected — staff
  // untick what won't fit on this run. Re-runs whenever the candidate set or
  // the destination filter changes, so switching lanes resets the picks.
  useEffect(() => {
    setSelectionModel(visibleRows.map((r) => r._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, nextStationFilter]);

  // With the filter required below whenever there's more than one reachable
  // destination, `visibleRows` (and therefore any selection made from it)
  // can never span more than one destination — no separate "mixed
  // destinations" check needed once submission is gated on this.
  const needsNextStationSelection = nextStationOptions.length > 1 && !nextStationFilter;

  const handleSubmit = () => {
    if (selectionModel.length === 0 || needsNextStationSelection) return;
    setIsSubmitting(true);
    CreateDepartureBatch(selectedStationId, selectionModel).then((res) => {
      setIsSubmitting(false);
      if (res && res.success) {
        playCorrectSound();
        const skippedCount = res.skipped?.length || 0;
        Swal.fire({
          icon: "success",
          title: t("departureBatch.submitSuccessTitle"),
          html: `<div>${t("departureBatch.submitSuccessBody", {
            count: res.data.items.length,
            batchNo: res.data.batchNo,
            toStation: res.data.toStationId,
          })}</div>${skippedCount > 0
              ? `<div style="margin-top:8px;color:#b45309;">${t("departureBatch.submitSkipped", { count: skippedCount })}</div>`
              : ""
            }`,
        });
        refreshCandidates(selectedStationId);
      } else {
        playIncorrectSound();
        Swal.fire({
          icon: "error",
          title: t("departureBatch.submitErrorTitle"),
          text: res?.message,
        });
      }
    });
  };

  const handleEditClick = (row) => {
    GetPackageDetailByTracking(row.tracking).then((res) => {
      if (res.status === 200 && res.data) {
        setEditPackage(res.data);
      }
    });
  };

  // Scan/type a tracking number to quickly toggle it in or out of the batch
  // without hunting for it in the grid — matches against the full candidate
  // set for this station (not just the current destination-filtered view).
  const handleSearchSubmit = (raw) => {
    const cleaned = getScanText(raw);
    setSearchTracking("");
    if (!cleaned) return;

    const row = rows.find((r) => r.tracking === cleaned);
    if (!row) {
      playAlertSound();
      Swal.fire({
        icon: "warning",
        title: t("departureBatch.searchNotFoundTitle"),
        text: t("departureBatch.searchNotFoundText", { tracking: cleaned }),
      });
      return;
    }

    const isSelected = selectionModel.includes(row._id);
    Swal.fire({
      icon: "question",
      title: isSelected
        ? t("departureBatch.confirmExcludeTitle")
        : t("departureBatch.confirmIncludeTitle"),
      html: `<div style="color:#534EED; font-size: 18px; font-weight:700;">${cleaned}</div>`,
      showCancelButton: true,
      confirmButtonText: isSelected
        ? t("departureBatch.confirmExclude")
        : t("departureBatch.confirmInclude"),
      cancelButtonText: t("common.cancel"),
    }).then((result) => {
      if (!result.isConfirmed) return;
      playCorrectSound();
      setSelectionModel((prev) =>
        isSelected ? prev.filter((id) => id !== row._id) : [...prev, row._id]
      );
    });
  };

  const columns = [
    {
      field: "tracking",
      headerName: t("departureBatch.columnTracking"),
      flex: 1,
      renderCell: (params) => (
        <Link
          component="button"
          color="secondary"
          underline="hover"
          onClick={() => setTrackingDetailNumber(params.row.tracking)}
        >
          {params.row.tracking}
        </Link>
      ),
    },
    {
      field: "customerName",
      headerName: t("departureBatch.columnCustomer"),
      flex: 1,
      valueGetter: (params) => params.row.customer?.name || "",
    },
    { field: "description", headerName: t("departureBatch.columnDescription"), flex: 1.5 },
    {
      field: "nextStationId",
      headerName: t("departureBatch.columnNextStation"),
      flex: 0.6,
      renderCell: (params) => (
        <Chip label={`→ ${params.value}`} size="small" color="secondary" variant="outlined" />
      ),
    },
    {
      field: "updatedAt",
      headerName: t("departureBatch.columnUpdatedAt"),
      flex: 0.8,
      valueGetter: (params) =>
        params.row.updatedAt
          ? moment(params.row.updatedAt).tz("Asia/Vientiane").format("DD/MM/YYYY, h:mm:ss A")
          : "",
    },
    {
      field: "edit",
      headerName: "",
      flex: 0.4,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => handleEditClick(params.row)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "flex-end",
          }}
        >
          {/* `size="small"` on both FormControls below brings the Select's
              height down to match the search box and Submit button (all
              ~40px) — the default MUI size renders taller and threw the row
              out of alignment. */}
          <FormControl size="small" sx={{ minWidth: 220, flex: "0 1 240px" }}>
            <InputLabel>{t("departureBatch.selectStation")}</InputLabel>
            <Select
              label={t("departureBatch.selectStation")}
              value={selectedStationId}
              onChange={(e) => setSelectedStationId(e.target.value)}
              required
            >
              {stationList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Only worth showing when this hub actually fans out to more
              than one destination (e.g. VTE -> DKC/PHT/THK) — a single-leg
              hub like NKH -> VTE has nothing to filter. */}
          {nextStationOptions.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 180, flex: "0 1 200px" }}>
              {/* `shrink` is required alongside `displayEmpty` below — with
                  an empty value MUI doesn't know to float the label out of
                  the way on its own, so it renders on top of the selected
                  value text ("Select station") instead of above it. */}
              <InputLabel shrink>{t("departureBatch.selectNextStation")}</InputLabel>
              <Select
                label={t("departureBatch.selectNextStation")}
                value={nextStationFilter}
                onChange={(e) => setNextStationFilter(e.target.value)}
                // MUI treats value="" as "nothing selected" and renders the
                // field blank by default, even though a matching MenuItem
                // exists — displayEmpty makes it show that MenuItem's
                // label ("Select station") instead of an empty box.
                displayEmpty
              >
                <MenuItem value="">{t("departureBatch.allStations")}</MenuItem>
                {nextStationOptions.map((id) => (
                  <MenuItem key={id} value={id}>
                    → {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Fills the remaining row width instead of sitting isolated on
              its own line below — keeps the whole control bar as one
              coherent group rather than two disconnected rows. */}
          {stationEligible && !loadError && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: "1 1 260px",
                minWidth: 220,
                height: "40px",
                background: BG_TINT,
                border: `1px solid ${BORDER}`,
                borderRadius: RADIUS_INPUT,
                px: "12px",
              }}
            >
              <InputBase
                placeholder={t("departureBatch.searchPlaceholder")}
                value={searchTracking}
                onChange={(e) => setSearchTracking(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchSubmit(e.target.value);
                    e.preventDefault();
                  }
                }}
                sx={{ flex: 1, fontSize: "0.9rem" }}
              />
              <IconButton size="small" onClick={() => handleSearchSubmit(searchTracking)}>
                <SearchIcon fontSize="small" />
              </IconButton>
              {isMobile && (
                <IconButton size="small" onClick={() => setCameraOpen(true)}>
                  <CameraAltIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          <Button
            variant="contained"
            color="secondary"
            startIcon={<LocalShippingOutlinedIcon />}
            disabled={!stationEligible || selectionModel.length === 0 || needsNextStationSelection || isSubmitting}
            onClick={handleSubmit}
            sx={{ height: "40px", flexShrink: 0 }}
          >
            {t("departureBatch.submit", { count: selectionModel.length })}
          </Button>
        </Box>
      </BentoCard>

      {loadError ? (
        <BentoCard bodySx={{ p: "48px 22px", textAlign: "center" }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1, color: "error.main" }} />
          <Typography color="error">{loadError}</Typography>
        </BentoCard>
      ) : !stationEligible ? (
        <BentoCard bodySx={{ p: "48px 22px", textAlign: "center" }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
          <Typography color="text.secondary">
            {t("departureBatch.notEligible")}
          </Typography>
        </BentoCard>
      ) : (
        <Box height="70vh" width="100%">
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id}
            rows={visibleRows}
            columns={columns}
            checkboxSelection
            selectionModel={selectionModel}
            onSelectionModelChange={(model) => setSelectionModel(model)}
            containerSx={{ height: "100%" }}
            autoHeight={false}
            sx={{
              "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": {
                color: PURPLE_MAIN,
              },
            }}
          />
        </Box>
      )}

      <TrackingDetailDialog
        open={!!trackingDetailNumber}
        onClose={() => setTrackingDetailNumber(null)}
        trackingNumber={trackingDetailNumber}
      />

      <PackageEditDialog
        open={!!editPackage}
        onClose={() => setEditPackage(null)}
        packageData={editPackage}
        onSuccess={() => {
          setEditPackage(null);
          refreshCandidates(selectedStationId);
        }}
      />

      <Scanner
        open={cameraOpen}
        setOpen={setCameraOpen}
        closeAfterResult
        onDecode={(result) => handleSearchSubmit(result)}
      />
    </>
  );
};

export default CreateTab;
